import React, { useEffect, useState } from "react";
import axios from "axios";
import "./EventList.css";
import { useHistory } from "react-router-dom";
import BuyCreditsPage from "./BuyCredits/BuyCreditsPage";
import CreateEventModal from "./CreateEvent/CreateEventModal";
import Walkthrough from "./Walkthrough/Walkthrough";

const baseURL = process.env.REACT_APP_API_BASE_URL;

const EventList = ({ eventId, userId }) => {
  const history = useHistory();
  let [eventsList, setEventsList] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [credits, setCredits] = useState(null);
  const [vendorPhone, setVendorPhone] = useState('');
  const [showBuyCreditsPage, setShowBuyCreditsPage] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [studioLogo, setStudioLogo] = useState('');
  const [studioName, setStudioName] = useState(localStorage.getItem('vendorDisplayName') || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const LIMIT = 10;
  const searchTimerRef = React.useRef(null);

  const handleLogout = () => {
    localStorage.clear();
    history.push("/");
  };

  const fetchEvents = async (vendorIdToFetch, search = '', page = 1) => {
    if (!vendorIdToFetch) return;
    try {
      setIsLoading(true);
      const requestParams = {
        vendor_id: vendorIdToFetch,
        type: 'all',
        page,
        limit: LIMIT,
        ...(search.length >= 3 && { search }),
      };
      const response = await axios.get(`${baseURL}/eventsByVendor`, {
        params: requestParams,
        headers: { 'x-api-key': process.env.REACT_APP_BROWSER_API_KEY },
      });
      const data = response.data;
      setEventsList(data.events || []);
      const total = data.total_count ?? data.total ?? data.totalCount ?? (data.events?.length ?? 0);
      setTotalPages(Math.max(1, Math.ceil(total / LIMIT)));
      setCurrentPage(page);
    } catch (error) {
      setEventsList([]);
    } finally {
      setIsLoading(false);
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(searchTimerRef.current);
    if (val.length === 0) {
      fetchEvents(vendorId, '', 1);
      return;
    }
    if (val.length < 3) return;
    setIsSearching(true);
    searchTimerRef.current = setTimeout(() => {
      fetchEvents(vendorId, val, 1);
    }, 400);
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    clearTimeout(searchTimerRef.current);
    fetchEvents(vendorId, '', 1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    fetchEvents(vendorId, searchQuery, page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleVendorIdSubmit = () => {
    if (vendorId.trim()) {
      fetchEvents(vendorId.trim());
    }
  };

  const fetchCredits = async (vid) => {
    try {
      const res = await fetch(`${baseURL}/vendor?vendor_id=${encodeURIComponent(vid)}`, {
        headers: { 'x-api-key': process.env.REACT_APP_BROWSER_API_KEY },
      });
      if (!res.ok) return;
      const data = await res.json();
      const vendor = data.vendor || data;
      setCredits(vendor.photo_credits ?? 0);
      if (vendor.studio_logo) setStudioLogo(vendor.studio_logo);
      if (vendor.studio_name) setStudioName(vendor.studio_name);
      const rawMobile = Array.isArray(vendor.mobile) ? vendor.mobile[0] : (vendor.mobile || '');
      setVendorPhone(rawMobile);
    } catch (_) {}
  };

  // Prefill vendorId from localStorage and auto-fetch
  useEffect(() => {
    try {
      const savedVendorId = localStorage.getItem('vendorId');
      if (savedVendorId) {
        setVendorId(savedVendorId);
        fetchEvents(savedVendorId, '', 1);
        fetchCredits(savedVendorId);
      }
      if (localStorage.getItem('walkthrough_pending')) {
        localStorage.removeItem('walkthrough_pending');
        setTimeout(() => setShowWalkthrough(true), 600);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (eventId && userId) {
      const now = new Date().toISOString();
      const singleEvent = [{
        category: "Custom",
        type: "Manual",
        title: eventId,
        from_date: now,
        to_date: now,
        event_id: eventId,
        image: "/images/wedding-image.jpg"
      }];
      setEventsList(singleEvent);
    } else {
      setEventsList([]);
    }

  }, [eventId, userId]);

  return (
    <div className="event-list-container">
      
      {/* ── Top Header & Profile ── */}
      <div className="header-event">
        <div className="header-text-block">
          <h1>
            Welcome back,<br />
            <span>
              {studioName ? `${studioName}!` : 'Photographer!'}
            </span>
          </h1>
          <p className="subtitle-event">Here are your recent events. Let’s make them memorable.</p>
        </div>
        
        <div className="search-container-event">
          {credits !== null && (
            <div className={`el-credits-badge${credits === 0 ? ' el-credits-badge--empty' : ''}`}>
              🪙 <span>{new Intl.NumberFormat('en-IN').format(credits)}</span> Credits
            </div>
          )}
          <button className="el-buy-credits-btn" onClick={() => setShowBuyCreditsPage(true)}>
            + Buy Credits
          </button>
<img
            src={studioLogo || `https://ui-avatars.com/api/?name=${encodeURIComponent(typeof window !== 'undefined' ? (localStorage.getItem('vendorDisplayName') || 'Photographer') : 'Photographer')}&background=F80353&color=fff&size=150`}
            alt="Profile"
            className="profile-photo-event"
            onClick={() => setShowDropdown(!showDropdown)}
            style={{ cursor: 'pointer' }}
          />
          <button
            className="el-tour-btn"
            onClick={() => setShowWalkthrough(true)}
            title="App tour"
          >?</button>
          {showDropdown && (
            <div className="profile-dropdown">
              <button className="profile-menu-btn" onClick={() => { setShowDropdown(false); history.push("/profile"); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                Edit Profile
              </button>
              <button className="profile-menu-btn" onClick={() => { setShowDropdown(false); history.push("/transactions"); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2"/>
                  <line x1="2" y1="10" x2="22" y2="10"/>
                </svg>
                Transactions
              </button>
              <button className="profile-menu-btn" onClick={() => { setShowDropdown(false); history.push("/portfolio"); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2"/>
                  <path d="M8 21h8M12 17v4"/>
                </svg>
                Portfolio
              </button>
              <button className="logout-btn" onClick={handleLogout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                Log out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Search ── */}
      <div className="event-toolbar">
        <button className="el-create-event-btn" onClick={() => setShowCreateEvent(true)}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Create Event
        </button>
        <div className="el-search-wrap">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="el-search-input"
            type="text"
            placeholder="Search events…"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {searchQuery && (
            <button className="el-search-clear" onClick={handleSearchClear}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* ── Event Grid ── */}
      <div className="event-cards-event">
        {isLoading || isSearching ? (
          <div className="loader-container">
            <div className="spinner"></div>
            <span>{isSearching ? 'Searching events…' : 'Fetching your events...'}</span>
          </div>
        ) : searchQuery.length > 0 && searchQuery.length < 3 ? (
          <p className="el-search-hint">Type at least 3 characters to search…</p>
        ) : eventsList.length === 0 ? (
          <h1 className="no-events">
            {searchQuery ? `No events found for "${searchQuery}".` : 'Create Event or You need to be added as "Photographer" in Easy Events App to view events.'}
          </h1>
        ) : eventsList.map((event, index) => (
            <div key={index} className="event-card" onClick={() => {
              try {
                if (event?.event_id) localStorage.setItem('currentEventId', event.event_id);
                if (vendorId) localStorage.setItem('currentUserId', vendorId);
                if (event?.title) localStorage.setItem('currentEventTitle', event.title);
                localStorage.setItem('currentPaidPackage', String(event?.paid_package ?? true));
                localStorage.setItem('currentDownloadEnabled', String(event?.download_enabled ?? true));
                localStorage.setItem('currentShareEnabled', String(event?.share_enabled ?? true));
                if (event?.event_logo) localStorage.setItem('currentLogoUrl', event.event_logo);
                else localStorage.removeItem('currentLogoUrl');
                if (event?.watermark) localStorage.setItem('currentWatermarkUrl', event.watermark);
                else localStorage.removeItem('currentWatermarkUrl');
              } catch (e) { /* ignore */ }
              history.push(`/folders`, {
                eventId: event.event_id,
                userId: localStorage.getItem('uid'),
                eventTitle: event.title
              });
            }}>
              <div className="event-image-container">
                <img
                  src={event.cover_photo || event.image || "/images/wedding-arch.jpg"}
                  alt={event.title}
                  className="event-image"
                  onError={(e)=>{e.target.onerror = null; e.target.src="/images/wedding-image.jpg"}}
                />

                <div className="event-date">
                  <span className="event-badge">
                    <span>●</span>
                    {new Date(event.from_date).toLocaleDateString("en-GB", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </div>
              </div>

              <div className="event-details-bottom">
                <p>{event.type || "Event"}</p>
                <h2>
                  {event.title}
                  <span className="card-arrow">→</span>
                </h2>
              </div>
            </div>
          ))
        }
      </div>

      {/* ── Pagination ── */}
      {!isLoading && totalPages > 1 && (
        <div className="el-pagination">
          <button
            className="el-page-btn el-page-btn--nav"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
            .reduce((acc, p, idx, arr) => {
              if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
              acc.push(p);
              return acc;
            }, [])
            .map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="el-page-ellipsis">…</span>
              ) : (
                <button
                  key={p}
                  className={`el-page-btn${p === currentPage ? ' el-page-btn--active' : ''}`}
                  onClick={() => handlePageChange(p)}
                >
                  {p}
                </button>
              )
            )}

          <button
            className="el-page-btn el-page-btn--nav"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      )}

      {showBuyCreditsPage && (
        <BuyCreditsPage
          onClose={() => setShowBuyCreditsPage(false)}
          onSuccess={() => fetchCredits(vendorId)}
          phone={vendorPhone}
        />
      )}

      {showCreateEvent && (
        <CreateEventModal
          onClose={() => setShowCreateEvent(false)}
          onSuccess={() => fetchEvents(vendorId, searchQuery, currentPage)}
        />
      )}

      {showWalkthrough && (
        <Walkthrough
          onDone={() => setShowWalkthrough(false)}
        />
      )}
    </div>
  );
};

export default EventList;
